import {
	apiAppBusinessBookingeventDelete,
	apiAppBusinessBookingeventSave,
	BookingEventActionType,
	BookingRuleType,
	type BookingEvent,
	type SaveBookingEventActionRequest,
	type SaveBookingEventRequest
} from '#lib/types/generated/index.js';
import { optimisticDelete, optimisticSave } from '#lib/stores/optimistic.js';
import { get, writable } from 'svelte/store';

export const bookingEventStore = writable<BookingEvent[]>([]);

function mapOptimisticRules(
	rulesConfig: SaveBookingEventRequest['rulesConfig']
): BookingEvent['rulesConfig'] {
	return rulesConfig.map((r) => {
		switch (r.type) {
			case BookingRuleType.Pax:
				return {
					type: BookingRuleType.Pax,
					minPax: r.minPax ?? null,
					maxPax: r.maxPax ?? null
				};
			case BookingRuleType.Offer:
				return { type: BookingRuleType.Offer, offerIds: r.offerIds ?? [] };
			case BookingRuleType.Product:
				return { type: BookingRuleType.Product, productIds: r.productIds ?? [] };
			case BookingRuleType.Activity:
				return { type: BookingRuleType.Activity, activityIds: r.activityIds ?? [] };
			case BookingRuleType.Session:
				return { type: BookingRuleType.Session, sessionIds: r.sessionIds ?? [] };
			case BookingRuleType.ValidityDates:
				return {
					type: BookingRuleType.ValidityDates,
					validityDates: r.validityDates ?? []
				};
			case BookingRuleType.Price:
				return {
					type: BookingRuleType.Price,
					minPrice: r.minPrice ?? null,
					maxPrice: r.maxPrice ?? null
				};
			case BookingRuleType.CustomerBookingCount:
				return {
					type: BookingRuleType.CustomerBookingCount,
					minCount: r.minCount ?? null,
					maxCount: r.maxCount ?? null
				};
		}
	});
}

export async function saveBookingEvent(
	request: SaveBookingEventRequest,
	id?: string
): Promise<BookingEvent> {
	const eventId = id ?? crypto.randomUUID();
	const existing = get(bookingEventStore).find((e) => e.id === eventId);
	const optimisticRulesConfig = mapOptimisticRules(request.rulesConfig);
	const optimisticAction = { ...request.action } as BookingEvent['action'];
	const ruleMatchMode = request.ruleMatchMode;
	const optimisticItem: BookingEvent = {
		...(existing ?? {
			id: eventId,
			name: request.name,
			enabled: request.enabled,
			timingTrigger: request.timingTrigger,
			timingOffsetMinutes: request.timingOffsetMinutes,
			action: optimisticAction,
			rulesConfig: optimisticRulesConfig,
			ruleMatchMode,
			archivedAt: null
		}),
		name: request.name,
		enabled: request.enabled,
		timingTrigger: request.timingTrigger,
		timingOffsetMinutes: request.timingOffsetMinutes,
		action: optimisticAction,
		rulesConfig: optimisticRulesConfig,
		ruleMatchMode
	};

	return optimisticSave(bookingEventStore, optimisticItem, () =>
		apiAppBusinessBookingeventSave(eventId, request)
	);
}

function mapActionToRequest(action: BookingEvent['action']): SaveBookingEventActionRequest {
	switch (action.type) {
		case BookingEventActionType.Email:
			return {
				type: BookingEventActionType.Email,
				subject: action.subject,
				message: action.message
			};
		case BookingEventActionType.Sms:
			return {
				type: BookingEventActionType.Sms,
				message: action.message
			};
		case BookingEventActionType.Webhook:
			return {
				type: BookingEventActionType.Webhook,
				url: action.url,
				method: action.method,
				headers: action.headers,
				payload: action.payload
			};
	}
}

export async function toggleBookingEvent(
	event: BookingEvent,
	targetEnabled?: boolean
): Promise<BookingEvent> {
	const newEnabled = targetEnabled ?? !event.enabled;
	const request: SaveBookingEventRequest = {
		name: event.name,
		enabled: newEnabled,
		ruleMatchMode: event.ruleMatchMode,
		timingTrigger: event.timingTrigger,
		timingOffsetMinutes: event.timingOffsetMinutes,
		action: mapActionToRequest(event.action),
		rulesConfig: event.rulesConfig.map((r) => ({
			...r,
			type: r.type as BookingRuleType
		}))
	};

	return saveBookingEvent(request, event.id);
}

export async function deleteBookingEvent(event: BookingEvent): Promise<void> {
	return optimisticDelete(bookingEventStore, event, () =>
		apiAppBusinessBookingeventDelete(event.id)
	);
}
